import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import leadsService, {
  type AccessRequestOutcome,
  type AccessRequestPayload,
} from '../../services/leads.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { readerCountryName } from '../../lib/countries';
import { readerLocale } from '../../i18n/language';
import PrivacyNoticeConsent from '../../components/PrivacyNoticeConsent';
import { usePrivacyNotice } from '../../hooks/usePrivacyNotice';
import { colors, gradients } from '../../styles/theme';

const SALES_FALLBACK_EMAIL = 'sales@qmi-investments.com';

/**
 * Public lead-acquisition form (locked 2026-05-11).
 *
 * Two render modes driven by query params:
 *  · default → form (email/name/phone/message)
 *  · `?internal_blocked=true` → block page that points the user to IT
 *
 * The form always starts empty. A person sent here after signing in with
 * an account we do not know types their details themselves: an address
 * and a name do not travel in a URL, where browser history, Referer
 * headers and access logs would keep them.
 */
export default function RequestAccess(): JSX.Element {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const internalBlocked = params.get('internal_blocked') === 'true';

  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');

  const privacy = usePrivacyNotice();
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState<AccessRequestOutcome | null>(null);
  const [outcomeMessage, setOutcomeMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (firstName.trim().length < 2 || lastName.trim().length < 2) {
      setError('First and last name must each be at least 2 characters.');
      return;
    }
    if (!accepted || !privacy.notice) {
      setError('Please read and accept the privacy notice to continue.');
      return;
    }
    setSubmitting(true);
    try {
      const payload: AccessRequestPayload = {
        privacy_notice_version: privacy.notice.version,
        email: email.trim(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: phone.trim() || undefined,
        message: message.trim() || undefined,
      };
      const res = await leadsService.submitAccessRequest(payload);
      setOutcome(res.outcome);
      setOutcomeMessage(res.message);
    } catch (e) {
      setError(getApiErrorMessage(e, 'Could not submit your request.'));
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Block page (internal email domain) ─────────────────────────
  if (internalBlocked) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <h1 style={titleStyle}>Internal email detected</h1>
          <p style={bodyStyle}>
            Please contact your IT team — they&apos;ll create your account through
            the back office and you&apos;ll receive a credential separately.
          </p>
          <p style={{ ...bodyStyle, fontSize: 13, color: colors.muted }}>
            If you believe this is a mistake, write to{' '}
            <a href={`mailto:${SALES_FALLBACK_EMAIL}`} style={linkStyle}>
              {SALES_FALLBACK_EMAIL}
            </a>{' '}
            and we&apos;ll route your request manually.
          </p>
          <button type="button" onClick={() => navigate('/login')} style={btnSecondaryStyle}>
            Back to sign-in
          </button>
        </div>
      </div>
    );
  }

  // ─── Success / outcome page ──────────────────────────────────────
  if (outcome !== null) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <h1 style={titleStyle}>
            {outcome === 'submitted' ? 'Request received' : 'Looks like we already know you'}
          </h1>
          <p style={bodyStyle}>{outcomeMessage}</p>
          {outcome === 'submitted' && (
            <p style={{ ...bodyStyle, fontSize: 13, color: colors.muted }}>
              We typically respond within 1–2 business days. Keep an eye on
              your inbox for the invitation email.
            </p>
          )}
          <Link to="/login" style={{ ...btnSecondaryStyle, display: 'inline-block', textDecoration: 'none' }}>
            Back to sign-in
          </Link>
        </div>
      </div>
    );
  }

  // ─── Form ────────────────────────────────────────────────────────
  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <h1 style={titleStyle}>Request access</h1>
        <p style={bodyStyle}>
          Tell us a bit about yourself and our team will reach out shortly.
        </p>

        {error && (
          <div style={errorBoxStyle} role="alert">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 12 }}>
          <label style={lblStyle}>
            Email <span style={requiredMarker}>*</span>
            <input
              type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={inputStyle} maxLength={255}
              autoComplete="email"
            />
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label style={lblStyle}>
              First name <span style={requiredMarker}>*</span>
              <input
                type="text" required value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                style={inputStyle} maxLength={120} minLength={2}
                autoComplete="given-name"
              />
            </label>
            <label style={lblStyle}>
              Last name <span style={requiredMarker}>*</span>
              <input
                type="text" required value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                style={inputStyle} maxLength={120} minLength={2}
                autoComplete="family-name"
              />
            </label>
          </div>
          <label style={lblStyle}>
            Phone <span style={{ color: colors.muted, fontSize: 12 }}>(optional)</span>
            <input
              type="tel" value={phone}
              onChange={(e) => setPhone(e.target.value)}
              style={inputStyle} maxLength={30}
              autoComplete="tel"
            />
          </label>
          <label style={lblStyle}>
            Why are you interested?{' '}
            <span style={{ color: colors.muted, fontSize: 12 }}>(optional)</span>
            <textarea
              value={message} rows={3} maxLength={2000}
              onChange={(e) => setMessage(e.target.value)}
              style={inputStyle}
              placeholder="What kind of investment opportunities are you looking for?"
            />
          </label>

          <PrivacyNoticeConsent
            state={privacy}
            checked={accepted}
            onChange={setAccepted}
            disabled={submitting}
          />

          <button type="submit" disabled={submitting || !accepted} style={btnPrimaryStyle}>
            {submitting ? 'Sending…' : 'Request access'}
          </button>
        </form>

        <p style={{ ...bodyStyle, fontSize: 13, color: colors.muted, marginTop: 16 }}>
          Prefer email? Write to{' '}
          <a href={`mailto:${SALES_FALLBACK_EMAIL}`} style={linkStyle}>
            {SALES_FALLBACK_EMAIL}
          </a>
        </p>
        <p style={{ fontSize: 13, color: colors.muted, marginTop: 8 }}>
          Already have an account?{' '}
          <Link to="/login" style={linkStyle}>Sign in</Link>
        </p>
        <WhereWeCannotServe />
      </div>
    </div>
  );
}

/**
 * Where we cannot open an account, said before someone fills in the form.
 *
 * The list is public for exactly this reason and nothing fetched it, so a
 * prospect in a blocked country typed everything in and was refused at the end.
 * It loads quietly: if the call fails, the form is unaffected — a prospect who
 * CAN be served must never be stopped by our inability to draw a list.
 */
function WhereWeCannotServe(): JSX.Element | null {
  const [countries, setCountries] = useState<string[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    leadsService
      .blockedCountries()
      .then((rows) =>
        setCountries(
          rows
            .map((c) => readerCountryName(c.country_code) || c.country_name)
            .sort((a, b) => a.localeCompare(b, readerLocale())),
        ),
      )
      .catch(() => setCountries([]));
  }, []);

  if (countries.length === 0) return null;

  return (
    <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${colors.border}` }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
          fontSize: 13, color: colors.ink, textDecoration: 'underline',
        }}
      >
        Countries we cannot open an account for
      </button>
      {open && (
        <p style={{ fontSize: 12, color: colors.muted, marginTop: 8, lineHeight: 1.6 }}>
          Our banking partner does not process payments to or from these countries, so we
          cannot hold an account for a resident of one: {countries.join(', ')}.
        </p>
      )}
    </div>
  );
}

const pageStyle: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: gradients.navy,
  padding: 24,
};
const cardStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 480,
  background: colors.surface,
  borderRadius: 16,
  padding: '32px 28px',
  boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
};
const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 22,
  color: colors.ink,
};
const bodyStyle: React.CSSProperties = {
  margin: '8px 0 16px',
  fontSize: 14,
  color: colors.inkSoft,
  lineHeight: 1.5,
};
const lblStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 13,
  color: colors.inkSoft,
};
const inputStyle: React.CSSProperties = {
  background: colors.surface,
  border: `1px solid ${colors.border}`,
  borderRadius: 8,
  padding: '8px 10px',
  color: colors.ink,
  fontSize: 14,
  // box-sizing keeps the input INSIDE the card on narrow viewports
  // (iPhone XR width = 414 px, card padding = 28 px each side).
  // Without it, the input's intrinsic width + padding pushed the field
  // past the card's right edge into the page gradient.
  width: '100%',
  boxSizing: 'border-box',
};
const btnPrimaryStyle: React.CSSProperties = {
  background: colors.ink,
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '10px 16px',
  cursor: 'pointer',
  fontWeight: 600,
  marginTop: 8,
  // Same reason as inputStyle — without box-sizing the button bled
  // past the card edge on mobile widths.
  width: '100%',
  boxSizing: 'border-box',
};
const btnSecondaryStyle: React.CSSProperties = {
  background: 'transparent',
  color: colors.ink,
  border: `1px solid ${colors.border}`,
  borderRadius: 8,
  padding: '8px 14px',
  cursor: 'pointer',
  fontWeight: 500,
  marginTop: 12,
};
const errorBoxStyle: React.CSSProperties = {
  background: colors.dangerBg,
  border: `1px solid ${colors.dangerBorder}`,
  color: colors.danger,
  padding: 10,
  borderRadius: 8,
  marginBottom: 12,
  fontSize: 13,
};
const linkStyle: React.CSSProperties = {
  color: colors.ink,
  textDecoration: 'underline',
};
const requiredMarker: React.CSSProperties = {
  color: colors.danger,
};
